import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Shield, CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";

function formatErr(d) {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || JSON.stringify(d);
}

export default function FounderClaim() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { user, refresh, loading: authLoading } = useAuth();

  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({ name: "", password: "" });

  useEffect(() => {
    if (!token) { setErr("This claim link is missing a token."); return; }
    (async () => {
      try {
        const { data } = await api.get(`/founder/claim/info?token=${encodeURIComponent(token)}`);
        setInfo(data);
      } catch (e) {
        setErr(formatErr(e.response?.data?.detail) || "Invalid or expired invite.");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr("");
    try {
      const payload = { token };
      if (info?.email_in_use === false && info?.founder_email) {
        if (!form.name || form.password.length < 6) {
          setErr("Enter your name and a password of 6+ characters.");
          setBusy(false);
          return;
        }
        payload.name = form.name;
        payload.password = form.password;
      }
      await api.post("/founder/claim", payload);
      await refresh();
      nav("/founder/dashboard");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Couldn't claim the business.");
    } finally { setBusy(false); }
  };

  // Permanent error state
  if (err && !info) {
    return (
      <div className="min-h-screen bg-[#F4F8F8]" style={{ fontFamily: "Manrope, sans-serif" }}>
        <div className="mx-auto max-w-md px-6 py-16 text-center">
          <Link to="/" className="text-2xl font-extrabold text-[#0A8B85]">Clinq</Link>
          <div className="mt-12 rounded-2xl border border-red-200 bg-white p-8" data-testid="claim-error">
            <AlertCircle className="mx-auto h-10 w-10 text-red-500"/>
            <h1 className="mt-4 text-xl font-bold text-neutral-900">Claim link unavailable</h1>
            <p className="mt-2 text-sm text-neutral-600">{err}</p>
            <Link to="/" className="mt-6 inline-block text-sm font-semibold text-[#0A8B85] hover:underline">
              Back to Clinq
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F8F8]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#0A8B85] border-t-transparent"/>
      </div>
    );
  }

  const isNewUser = info.email_in_use === false && info.founder_email;
  const isExistingFounder = info.email_in_use === true;
  const isLoggedInRightUser = isExistingFounder && user?.email === info.founder_email && user?.role === "founder";

  return (
    <div className="min-h-screen bg-[#F4F8F8]" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="mx-auto max-w-md px-6 py-16">
        <Link to="/" className="text-2xl font-extrabold text-[#0A8B85]" data-testid="claim-logo">Clinq</Link>

        <div className="mt-8 rounded-2xl border border-[#0A8B85]/30 bg-white p-6" data-testid="claim-context">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-[#0A8B85]/10 p-2.5">
              <Shield className="h-5 w-5 text-[#0A8B85]"/>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#0A8B85]">Business invitation</p>
              <h1 className="mt-1 text-xl font-bold text-neutral-900">{info.company?.company_name || "Untitled business"}</h1>
              <p className="mt-1 text-sm text-neutral-600">
                {info.company?.sector} · {info.company?.country} · {info.company?.stage}
              </p>
              {info.admin_name && (
                <p className="mt-2 text-xs text-neutral-500">
                  Prepared for you by <b className="text-neutral-700">{info.admin_name}</b> on behalf of Clinq.
                </p>
              )}
            </div>
          </div>
        </div>

        {isNewUser && (
          <>
            <h2 className="mt-8 text-2xl font-bold text-neutral-900">Set up your account</h2>
            <p className="mt-1 text-sm text-neutral-600">
              We'll create your founder account and transfer this business to you.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Email</label>
                <input value={info.founder_email} disabled
                  className="mt-1 w-full rounded-lg border border-neutral-200 bg-neutral-100 px-4 py-3 text-sm text-neutral-700"
                  data-testid="claim-email"/>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Your name</label>
                <input data-testid="claim-name" required value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                  className="mt-1 w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85]"/>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Password</label>
                <input data-testid="claim-password" type="password" required minLength={6} value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  placeholder="Min 6 characters"
                  className="mt-1 w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85]"/>
              </div>
              {err && <p className="text-sm text-red-600" data-testid="claim-err">{err}</p>}
              <button data-testid="claim-submit" disabled={busy} type="submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#0A8B85] py-3 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
                {busy ? "Claiming…" : (<>Claim {info.company?.company_name || "business"} <ArrowRight className="h-4 w-4"/></>)}
              </button>
            </form>
          </>
        )}

        {isExistingFounder && (
          <div className="mt-8 rounded-2xl border border-neutral-200 bg-white p-6" data-testid="claim-existing">
            <h2 className="text-xl font-bold text-neutral-900">An account already exists for this email</h2>
            <p className="mt-2 text-sm text-neutral-600">
              Log in as <b>{info.founder_email}</b> to claim this business.
            </p>
            {!authLoading && !isLoggedInRightUser && (
              <Link to={`/login?next=${encodeURIComponent(`/founder/claim?token=${token}`)}`}
                className="mt-4 inline-flex items-center gap-1 rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-bold text-white hover:bg-[#066667]"
                data-testid="claim-to-login">
                Log in to claim
              </Link>
            )}
            {!authLoading && isLoggedInRightUser && (
              <>
                {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
                <button onClick={submit} disabled={busy} data-testid="claim-submit"
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#0A8B85] px-5 py-2 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
                  {busy ? "Claiming…" : (<>Confirm claim <CheckCircle2 className="h-4 w-4"/></>)}
                </button>
              </>
            )}
          </div>
        )}

        {!isNewUser && !isExistingFounder && (
          <div className="mt-8 rounded-2xl border border-neutral-200 bg-white p-6" data-testid="claim-no-email">
            <h2 className="text-xl font-bold text-neutral-900">Log in or sign up to claim</h2>
            <p className="mt-2 text-sm text-neutral-600">
              This invite isn't tied to a specific email. Sign in as a founder (or create a founder account) then re-open this link.
            </p>
            <div className="mt-4 flex gap-2">
              <Link to={`/signup?role=founder&next=${encodeURIComponent(`/founder/claim?token=${token}`)}`}
                className="rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-bold text-white hover:bg-[#066667]" data-testid="claim-to-signup">
                Sign up
              </Link>
              <Link to={`/login?next=${encodeURIComponent(`/founder/claim?token=${token}`)}`}
                className="rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50" data-testid="claim-to-login">
                Log in
              </Link>
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-neutral-500">
          Invite expires {new Date(info.expires_at).toLocaleString()}.
        </p>
      </div>
    </div>
  );
}
